import {
  buildIdempotencyKey,
  generateMatchRecordId,
  generateParticipantId,
  MATCH_RECORD_SCHEMA_VERSION,
  MATCH_RECORD_STATUS,
} from "./matchRecordSchema";
import { normalizeSportId, DEFAULT_SPORT } from "../sport/sportTypes";
import { resolveScoringSystem } from "../../sports/resolveScoringSystem";

/**
 * @param {Array<{ scoreA?: number; scoreB?: number; winner?: string }>} sets
 */
export function formatScoreSummary(sets) {
  return (sets || [])
    .map((s) => `${s.scoreA ?? 0}-${s.scoreB ?? 0}`)
    .join(", ");
}

/**
 * @param {Object} input
 * @param {string} input.sessionRoomId
 * @param {{ name: string; slots?: import('../../features/registry/models/registryTypes').MatchParticipantSnapshot[]; type?: string }} input.teamA
 * @param {{ name: string; slots?: import('../../features/registry/models/registryTypes').MatchParticipantSnapshot[]; type?: string }} input.teamB
 * @param {Array<{ scoreA?: number; scoreB?: number; winner?: string }>} input.sets
 * @param {string} input.winnerSide A | B
 * @param {string} input.createdBy
 * @param {number} input.completedAtMs
 * @param {number} [input.durationSec]
 * @param {string} [input.scoringFormat]
 * @param {string} [input.scoringSystem] rally | side_out
 * @param {string} [input.sportId]
 * @param {import('../../features/registry/models/registryTypes').MatchParticipantSnapshot[]} [input.participantSnapshots]
 * @param {string | null} [input.competitionId]
 * @param {string | null} [input.clubId]
 * @param {string} [input.source]
 * @param {number | null} [input.legacyRoomIndex]
 * @param {string} [input.matchId]
 */
export function buildCanonicalMatchPayload(input) {
  const matchId = input.matchId || generateMatchRecordId();
  const sportId = normalizeSportId(input.sportId || DEFAULT_SPORT);
  const scoringSystem = input.scoringSystem || resolveScoringSystem(sportId);
  const matchType =
    input.teamA?.type === "doubles" || input.teamB?.type === "doubles"
      ? "doubles"
      : "singles";
  const completedAtMs = input.completedAtMs || Date.now();
  const idempotencyKey =
    input.idempotencyKey ||
    buildIdempotencyKey(input.sessionRoomId || matchId, completedAtMs, input.winnerSide);
  const scoreSummary = formatScoreSummary(input.sets);
  const winnerName =
    input.winnerSide === "A" ? input.teamA?.name : input.teamB?.name;

  /** @type {import('./matchHistoryTypes').MatchRecord} */
  const record = {
    matchId,
    sportId,
    matchType,
    createdAt: completedAtMs,
    completedAt: completedAtMs,
    durationSec: input.durationSec ?? null,
    scoringFormat: input.scoringFormat || "",
    scoringSystem,
    winnerSide: input.winnerSide,
    result: `${winnerName} won (${scoreSummary})`,
    competitionId: input.competitionId ?? null,
    clubId: input.clubId ?? null,
    courtId: null,
    venueId: null,
    createdBy: input.createdBy,
    status: MATCH_RECORD_STATUS.COMPLETED,
    sessionRoomId: input.sessionRoomId || null,
    idempotencyKey,
    schemaVersion: MATCH_RECORD_SCHEMA_VERSION,
    sets: (input.sets || []).map((s) => ({
      scoreA: s.scoreA ?? 0,
      scoreB: s.scoreB ?? 0,
      winner: s.winner || "",
    })),
    source: input.source || "live",
    legacyRoomIndex: input.legacyRoomIndex ?? null,
    rallySummary: input.rallySummary || null,
  };

  const snapshots = input.participantSnapshots || [];
  const participants = snapshots.map((snap) => {
    const won = snap.team === input.winnerSide;
    const partner = snapshots.find(
      (p) => p.team === snap.team && p.position !== snap.position
    );
    return {
      participantId: generateParticipantId(),
      matchId,
      sportId,
      registryEntryId: snap.registryEntryId,
      playerId: snap.playerId,
      displayName: snap.displayName,
      avatar: snap.avatar ?? null,
      legacyPlayerName: null,
      team: snap.team,
      partnerPlayerId: partner?.playerId ?? null,
      position: snap.position,
      result: won ? "Win" : "Loss",
      scoreSummary,
      matchType,
      completedAt: completedAtMs,
      competitionId: record.competitionId,
      clubId: record.clubId,
      createdBy: input.createdBy,
    };
  });

  return { record, participants, idempotencyKey };
}
